import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

interface EspecialidadItem {
  nombre: string
  total: number
}

interface LineaItem {
  nombre: string
  total: number
  especialidades: EspecialidadItem[]
}

interface FerrerMedicosPeru0218Data {
  titulo: string
  subtitulo: string
  total_general: number
  total_lineas: number
  total_especialidades: number
  lineas: LineaItem[]
  especialidades_unicas: string[]
  datos_raw: { Linea?: Record<string, Record<string, number>> }
}

interface Registro {
  linea: string
  especialidad: string
  total: number
}

const COLORES = [
  'rgba(59, 130, 246, 0.7)',   // blue-500
  'rgba(16, 185, 129, 0.7)',   // emerald-500
  'rgba(139, 92, 246, 0.7)',   // violet-500
  'rgba(245, 158, 11, 0.7)',   // amber-500
  'rgba(239, 68, 68, 0.7)',    // red-500
  'rgba(236, 72, 153, 0.7)',   // pink-500
  'rgba(14, 165, 233, 0.7)',   // sky-500
  'rgba(168, 85, 247, 0.7)',   // purple-500
  'rgba(34, 197, 94, 0.7)',    // green-500
  'rgba(251, 146, 60, 0.7)',   // orange-400
]

const COLORES_BORDE = [
  '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b',
  '#ef4444', '#ec4899', '#0ea5e9', '#a855f7',
  '#22c55e', '#fb923c',
]

const COLORES_LINEA: Record<string, { bg: string; border: string; badgeBg: string; badgeText: string }> = {
  'Sistema Nervioso Central': { bg: 'rgba(139, 92, 246, 0.7)', border: '#8b5cf6', badgeBg: 'bg-violet-100', badgeText: 'text-violet-800' },
  'Gastroenterología': { bg: 'rgba(16, 185, 129, 0.7)', border: '#10b981', badgeBg: 'bg-emerald-100', badgeText: 'text-emerald-800' },
}

const COLORES_LINEA_FALLBACK = [
  { bg: 'rgba(59, 130, 246, 0.7)', border: '#3b82f6', badgeBg: 'bg-blue-100', badgeText: 'text-blue-800' },
  { bg: 'rgba(245, 158, 11, 0.7)', border: '#f59e0b', badgeBg: 'bg-amber-100', badgeText: 'text-amber-800' },
]

export function FerrerMedicosPeru0218Page() {
  const { data, isLoading, error } =
    useDashReport<FerrerMedicosPeru0218Data>('ferrer/medicos-peru-0218')

  const [filtroLinea, setFiltroLinea] = useState('todas')
  const [filtroEspecialidad, setFiltroEspecialidad] = useState('todas')
  const [seccionExpandida, setSeccionExpandida] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const chevronRef = useRef<SVGSVGElement>(null)

  // Build flat records from datos_raw (same as Django template JS)
  const registros = useMemo<Registro[]>(() => {
    if (!data?.datos_raw) return []
    const lineasData = (data.datos_raw as { Linea?: Record<string, Record<string, number>> }).Linea || {}
    const result: Registro[] = []
    for (const [lineaNombre, especialidades] of Object.entries(lineasData)) {
      for (const [espNombre, total] of Object.entries(especialidades)) {
        result.push({ linea: lineaNombre, especialidad: espNombre, total })
      }
    }
    return result
  }, [data])

  // All line names from datos_raw for fallback color indexing
  const lineasDataKeys = useMemo(() => {
    if (!data?.datos_raw) return []
    const lineasData = (data.datos_raw as { Linea?: Record<string, Record<string, number>> }).Linea || {}
    return Object.keys(lineasData)
  }, [data])

  const getColorLinea = useCallback((nombre: string) => {
    if (COLORES_LINEA[nombre]) return COLORES_LINEA[nombre]
    const keys = lineasDataKeys.filter(k => !COLORES_LINEA[k])
    const idx = keys.indexOf(nombre)
    return COLORES_LINEA_FALLBACK[idx >= 0 ? idx % COLORES_LINEA_FALLBACK.length : 0]
  }, [lineasDataKeys])

  // Filtered records
  const registrosFiltrados = useMemo(() => {
    return registros.filter(r => {
      const matchLinea = filtroLinea === 'todas' || r.linea === filtroLinea
      const matchEsp = filtroEspecialidad === 'todas' || r.especialidad === filtroEspecialidad
      return matchLinea && matchEsp
    })
  }, [registros, filtroLinea, filtroEspecialidad])

  const totalFiltrado = useMemo(() => {
    return registrosFiltrados.reduce((s, r) => s + r.total, 0)
  }, [registrosFiltrados])

  // Available specialties based on current line filter
  const especialidadesDisponibles = useMemo(() => {
    const filtered = filtroLinea === 'todas'
      ? registros
      : registros.filter(r => r.linea === filtroLinea)
    const espMap: Record<string, number> = {}
    filtered.forEach(r => {
      espMap[r.especialidad] = (espMap[r.especialidad] || 0) + r.total
    })
    return Object.entries(espMap)
      .sort((a, b) => b[1] - a[1])
      .map(([nombre, total]) => ({ nombre, total }))
  }, [registros, filtroLinea])

  // Visible lines and specialties for stats
  const lineasVisibles = useMemo(() => {
    return [...new Set(registrosFiltrados.map(r => r.linea))]
  }, [registrosFiltrados])

  const espsVisibles = useMemo(() => {
    return [...new Set(registrosFiltrados.map(r => r.especialidad))]
  }, [registrosFiltrados])

  // Title/label logic
  const tituloResumen = useMemo(() => {
    if (filtroLinea === 'todas' && filtroEspecialidad === 'todas') return 'Resumen General'
    if (filtroLinea !== 'todas' && filtroEspecialidad === 'todas') return filtroLinea
    return filtroEspecialidad
  }, [filtroLinea, filtroEspecialidad])

  const labelTotal = useMemo(() => {
    if (filtroLinea === 'todas' && filtroEspecialidad === 'todas') return 'Total Médicos'
    if (filtroLinea !== 'todas' && filtroEspecialidad === 'todas') return 'Total ' + filtroLinea
    return 'Total ' + filtroEspecialidad
  }, [filtroLinea, filtroEspecialidad])

  // Handle line filter
  const handleFiltrarLinea = useCallback((nombre: string) => {
    setFiltroLinea(nombre)
    setFiltroEspecialidad('todas')
  }, [])

  // Handle specialty filter
  const handleFiltrarEspecialidad = useCallback((nombre: string) => {
    setFiltroEspecialidad(nombre)
  }, [])

  // Toggle collapsible section
  const toggleSeccion = useCallback(() => {
    setSeccionExpandida(prev => !prev)
  }, [])

  useEffect(() => {
    const content = contentRef.current
    const chevron = chevronRef.current
    if (!content || !chevron) return

    if (seccionExpandida) {
      content.style.maxHeight = content.scrollHeight + 'px'
      content.style.opacity = '1'
      chevron.style.transform = 'rotate(180deg)'
      chevron.classList.add('expanded')
    } else {
      content.style.maxHeight = '0px'
      content.style.opacity = '0'
      chevron.style.transform = 'rotate(0deg)'
      chevron.classList.remove('expanded')
    }
  }, [seccionExpandida, registrosFiltrados])

  // Chart: horizontal bar by specialty
  const chartEspData = useMemo(() => {
    const espMap: Record<string, number> = {}
    registrosFiltrados.forEach(r => {
      espMap[r.especialidad] = (espMap[r.especialidad] || 0) + r.total
    })
    const espOrdenadas = Object.entries(espMap).sort((a, b) => b[1] - a[1])
    return {
      labels: espOrdenadas.map(e => e[0]),
      datasets: [{
        label: 'Total Médicos',
        data: espOrdenadas.map(e => e[1]),
        backgroundColor: COLORES.slice(0, espOrdenadas.length),
        borderColor: COLORES_BORDE.slice(0, espOrdenadas.length),
        borderWidth: 1,
      }],
    }
  }, [registrosFiltrados])

  const chartEspOptions = useMemo(() => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 80 } },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'right' as const,
        offset: 4,
        clip: false,
        color: '#6b7280',
        font: { size: 11, weight: 500 as const },
        formatter: (v: number) => v.toLocaleString('es-PE'),
      },
    },
    scales: {
      x: { display: false },
    },
  }), [])

  // Chart: vertical bar by line
  const chartLineaData = useMemo(() => {
    const lineaMap: Record<string, number> = {}
    registrosFiltrados.forEach(r => {
      lineaMap[r.linea] = (lineaMap[r.linea] || 0) + r.total
    })
    const lineaOrdenadas = Object.entries(lineaMap).sort((a, b) => b[1] - a[1])
    return {
      labels: lineaOrdenadas.map(l => l[0]),
      datasets: [{
        label: 'Total Médicos',
        data: lineaOrdenadas.map(l => l[1]),
        backgroundColor: lineaOrdenadas.map(l => getColorLinea(l[0]).bg),
        borderColor: lineaOrdenadas.map(l => getColorLinea(l[0]).border),
        borderWidth: 1,
      }],
    }
  }, [registrosFiltrados, getColorLinea])

  const chartLineaOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 30 } },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'top' as const,
        offset: 2,
        clip: false,
        color: '#6b7280',
        font: { size: 12, weight: 500 as const },
        formatter: (value: number) => {
          const pct = totalFiltrado > 0 ? ((value / totalFiltrado) * 100).toFixed(1) : '0.0'
          return value.toLocaleString('es-PE') + ' (' + pct + '%)'
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grace: '10%',
        ticks: {
          callback: (v: string | number) => Number(v).toLocaleString('es-PE'),
        },
      },
    },
  }), [totalFiltrado])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
        .animate-pulse-subtle.expanded { animation: none; opacity: 1; transform: scale(1); }
      `}</style>
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center">
                <img
                  src="/logo-tonal-negro.png"
                  alt="Tonal"
                  className="h-8 lg:h-10 w-auto mr-4 lg:mr-6"
                />
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{data.titulo}</h1>
                  <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href="https://enterprise.tonalstrategies.com/pricing/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
                >
                  Calculador
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Filtro por Linea */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span className="font-semibold text-gray-900">Filtrar por Línea:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleFiltrarLinea('todas')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filtroLinea === 'todas'
                      ? 'bg-violet-600 text-white hover:bg-violet-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Todas
                </button>
                {data.lineas.map(linea => (
                  <button
                    key={linea.nombre}
                    onClick={() => handleFiltrarLinea(linea.nombre)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      filtroLinea === linea.nombre
                        ? 'bg-violet-600 text-white hover:bg-violet-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {linea.nombre} <span className="text-xs opacity-75">({Math.round(linea.total)})</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Filtro por Especialidad */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="font-semibold text-gray-900">Filtrar por Especialidad:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleFiltrarEspecialidad('todas')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filtroEspecialidad === 'todas'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Todas
                </button>
                {especialidadesDisponibles.map(esp => (
                  <button
                    key={esp.nombre}
                    onClick={() => handleFiltrarEspecialidad(esp.nombre)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      filtroEspecialidad === esp.nombre
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {esp.nombre} <span className="text-xs opacity-75">({esp.total.toLocaleString('es-PE')})</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Estadisticas Generales */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>{tituloResumen}</span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-3xl lg:text-4xl font-bold text-gray-900">
                  {totalFiltrado.toLocaleString('es-PE')}
                </p>
                <p className="text-sm text-gray-600 mt-1">{labelTotal}</p>
              </div>
              <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-2xl lg:text-3xl font-bold text-purple-600">
                  {lineasVisibles.length}
                </p>
                <p className="text-sm text-gray-600 mt-1">Líneas</p>
              </div>
              <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-2xl lg:text-3xl font-bold text-blue-600">
                  {espsVisibles.length}
                </p>
                <p className="text-sm text-gray-600 mt-1">Especialidades</p>
              </div>
            </div>
          </div>

          {/* Graficas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Grafica por Especialidad (barras horizontales) */}
            <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Distribución por Especialidad
              </h3>
              <div className="h-64 lg:h-96">
                <Bar
                  key={'esp-' + filtroLinea + '-' + filtroEspecialidad}
                  data={chartEspData}
                  options={chartEspOptions}
                  plugins={[ChartDataLabels]}
                />
              </div>
            </div>

            {/* Grafica por Linea (barras verticales) */}
            <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
                Distribución por Línea
              </h3>
              <div className="h-64 lg:h-96">
                <Bar
                  key={'linea-' + filtroLinea + '-' + filtroEspecialidad}
                  data={chartLineaData}
                  options={chartLineaOptions}
                  plugins={[ChartDataLabels]}
                />
              </div>
            </div>
          </div>

          {/* Tabla de Detalle */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <button
              onClick={toggleSeccion}
              className="w-full text-left focus:outline-none group py-3 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
            >
              <div className="font-semibold text-gray-900 flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-base lg:text-lg">Detalle por Línea y Especialidad</span>
                  <span className="text-xs lg:text-sm text-gray-500 ml-2">
                    ({registrosFiltrados.length} registros)
                  </span>
                </div>
                <svg
                  ref={chevronRef}
                  className="w-5 h-5 text-gray-400 transform transition-transform duration-300 animate-pulse-subtle"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            <div
              ref={contentRef}
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{ maxHeight: 0, opacity: 0 }}
            >
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Línea</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Especialidad</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Médicos</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% del Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.lineas.map(linea =>
                      linea.especialidades.map(esp => {
                        const matchLinea = filtroLinea === 'todas' || linea.nombre === filtroLinea
                        const matchEsp = filtroEspecialidad === 'todas' || esp.nombre === filtroEspecialidad
                        if (!matchLinea || !matchEsp) return null
                        const porcentaje = totalFiltrado > 0
                          ? ((esp.total / totalFiltrado) * 100).toFixed(1)
                          : '0.0'
                        const colorLinea = getColorLinea(linea.nombre)
                        return (
                          <tr
                            key={linea.nombre + '-' + esp.nombre}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorLinea.badgeBg} ${colorLinea.badgeText}`}>
                                {linea.nombre}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{esp.nombre}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600">
                              {Math.round(esp.total)}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-600">
                              {porcentaje}%
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={2} className="px-4 py-3 text-sm font-bold text-gray-900">Total</td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-blue-700">
                        {totalFiltrado.toLocaleString('es-PE')}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-700">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 py-6 text-center">
          <div className="text-xs text-gray-400">
            <a href="https://enterprise.tonalstrategies.com" className="hover:text-gray-600 transition-colors">
              Tonal Enterprise
            </a>
            <span className="mx-2">&bull;</span>
            <span>2026 Tonal Strategies</span>
          </div>
        </footer>
      </div>
    </>
  )
}
